import { announceSignInNeeded, NeedsSignIn } from '../auth/session.tsx';
import { noteServedBuild } from '../build.ts';

export class ApiError extends Error {
  status: number;
  hint: string | undefined;
  /** The whole error body, for a refusal that carries more than words (a 409's candidates, 123). */
  body: unknown;
  constructor(status: number, message: string, hint?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.hint = hint;
    this.body = body;
  }
}

export type TokenSource = () => Promise<string>;

/**
 * Every request the app makes. Same origin, always under /api.
 *
 * A 401 means the token was rejected rather than absent — the session ran out
 * between the check and the call. Surfacing it as NeedsSignIn lets the shell
 * redirect once, rather than each screen inventing its own handling.
 */
export async function apiFetch<T>(
  path: string,
  getToken: TokenSource,
  init?: RequestInit,
): Promise<T> {
  const token = await getToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      authorization: `Bearer ${token}`,
      accept: 'application/json',
    },
  });
  // Is this page older than the build the server now serves? (build.ts)
  noteServedBuild(res.headers.get('x-procura-web'));

  // Told once, to the shell, which swaps to the sign-in screen. Still thrown,
  // so the caller stops — but no screen has to render it, and none should:
  // "your session ended" and "the server is unreachable" are different facts
  // and were being shown together.
  if (res.status === 401) {
    announceSignInNeeded('rejected');
    throw new NeedsSignIn();
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let hint: string | undefined;
    let body: unknown;
    try {
      body = await res.json();
      const b = body as { error?: string; hint?: string };
      if (b.error) message = b.error;
      hint = b.hint;
    } catch {
      // Non-JSON error body; the status line is all we have.
    }
    throw new ApiError(res.status, message, hint, body);
  }

  return (await res.json()) as T;
}

/**
 * A search: POST, with the text and every filter in the JSON body, never in the
 * URL (Arthur, 16 October). App Service logs every URL's query string, and a
 * search box is where a client's name is typed. Empty values are left out and
 * the rest sent as strings, exactly as a query string carried them, so the
 * server's parsing is unchanged.
 */
export function apiSearch<T>(path: string, params: object, getToken: TokenSource): Promise<T> {
  const body: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') body[key] = String(value);
  }
  return apiFetch<T>(path, getToken, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Unauthenticated, and the only call made before there is a session. */
export async function fetchAuthConfig<T>(): Promise<T> {
  const res = await fetch('/api/auth/config', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`The server did not report an auth mode (${res.status})`);
  return (await res.json()) as T;
}
